import React from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { MembershipStatus } from '../../graphql/__generated__/schema';
import { PortalUserDocument, ReinstateMembershipDocument } from '../../graphql/__generated__/types';
import { reinstateRefusalOf } from './reinstateRefusal';
import { ClosureState, closureAuthorSentence, closureReasonSentence } from './revocation';

interface Props {
    membershipId: string;
    organizationName: string;
    roleName: string;
    /** The closure being undone, already read against `status` by the sheet. */
    closure: ClosureState;
    onClose: () => void;
    /** Reopening sends no mail, so a never-activated person comes back « invited » with
     *  nothing to activate; the sheet chains on the resend. */
    onResendInvitation: () => void;
}

/**
 * The returned status decides which ending this is: ACTIVE signs in as before, INVITED means
 * the access exists and nothing can reach it, so its success screen is the next step, not a
 * confirmation. The closure motive is shown before the click so a reopening is not decided blind.
 */
const ReinstateMembershipDialog = ({
    membershipId,
    organizationName,
    roleName,
    closure,
    onClose,
    onResendInvitation,
}: Props) => {
    const [reinstate, { data, loading, error }] = useMutation(ReinstateMembershipDocument, {
        refetchQueries: [PortalUserDocument],
        awaitRefetchQueries: true,
    });

    const result = data?.reinstateMembership;

    const submit = () => {
        reinstate({ variables: { membershipId } }).catch(() => {});
    };

    /* ------------------------------------------------------------------- done */
    if (result) {
        // INVITED is the half-finished ending: reopened access with no way in until re-invited.
        const needsInvitation = result.status === MembershipStatus.Invited;

        return (
            <Modal open onClose={onClose} title={c('Reinstate access').t`Access reopened`}>
                <ModalContent>
                    <Alert
                        type={needsInvitation ? 'warning' : 'success'}
                        title={
                            needsInvitation
                                ? c('Reinstate access').t`The access is open again, but nobody has been told.`
                                : c('Reinstate access').t`${organizationName} is open to this person again.`
                        }
                    >
                        <p className="text-sm">
                            {needsInvitation
                                ? c('Reinstate access')
                                      .t`Reopening sends no email, and the closure had cancelled the invitation that was pending. This person has never activated their account: the access exists and nothing can reach it until a new activation link is sent.`
                                : c('Reinstate access')
                                      .t`They had already activated their account, so they sign in as before and find this account again, with the role and the perimeter they had.`}
                        </p>
                    </Alert>

                    <p className="mt-4 text-xs text-text-600">
                        {c('Reinstate access')
                            .t`The closure stays on record, with its date and its reason. It now reads as a past event on the sheet, not as the state of the access.`}
                    </p>
                </ModalContent>
                <ModalFooter>
                    {needsInvitation ? (
                        <>
                            <Button category="weak" onClick={onClose}>
                                {c('Action').t`Close`}
                            </Button>
                            <Button category="default" onClick={onResendInvitation}>
                                {c('Reinstate access').t`Send the invitation`}
                            </Button>
                        </>
                    ) : (
                        <Button category="default" onClick={onClose}>
                            {c('Action').t`Close`}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        );
    }

    /* ---------------------------------------------------------------- confirm */
    const authorSentence = closureAuthorSentence(closure);
    const reasonSentence = closureReasonSentence(closure);

    return (
        <Modal open onClose={onClose} title={c('Reinstate access').t`Reopen this access`}>
            <ModalContent>
                {authorSentence && (
                    <Alert type="warning" title={authorSentence}>
                        <p className="text-sm">{reasonSentence}</p>
                    </Alert>
                )}

                <p className="mt-4 text-xs text-text-600">
                    {c('Reinstate access')
                        .t`Reopening restores the access to ${organizationName} exactly as it was: the role « ${roleName} » and the perimeter that went with it. The gesture takes no role of its own.`}
                </p>
                <p className="mt-2 text-xs text-text-600">
                    {c('Reinstate access')
                        .t`No email leaves: if this person never activated their account, sending them an invitation is a second gesture, offered right after.`}
                </p>

                {error && (
                    <div className="mt-4">
                        <RefusalNotice refusal={reinstateRefusalOf(error)} />
                    </div>
                )}
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button category="default" onClick={submit} loading={loading}>
                    {c('Reinstate access').t`Reopen`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ReinstateMembershipDialog;
